import { useEffect, useState } from 'react'

/**
 * Note: OneTrust SDK is already loaded by OneTrustCookies.tsx in the app.
 * This file only provides utilities to check consent status and listen to consent changes.
 */

declare global {
  interface Window {
    /**
     * OneTrust object
     * @see {@link https://my.onetrust.com/s/article/UUID-d8291f61-aa31-813a-ef16-3f6dec73d643?language=en_US&topicId=0TO1Q000000ssJBWAY|OneTrust docs - Banner SDK JavaScript API}
     */
    OneTrust: {
      /**
       * Opens the preference center.
       * @see {@link https://my.onetrust.com/s/article/UUID-d8291f61-aa31-813a-ef16-3f6dec73d643#UUID-d8291f61-aa31-813a-ef16-3f6dec73d643_section-idm231900119985245|OneTrust docs - OneTrust.ToggleInfoDisplay()}
       */
      ToggleInfoDisplay: () => void
    }
    /**
     * Data layer object `OnetrustActiveGroups` with the cookie categories that the user has consented to.
     * @see {@link https://my.onetrust.com/s/article/UUID-66bcaaf1-c7ca-5f32-6760-c75a1337c226?topicId=0TO1Q000000ssJBWAY|OneTrust docs - OnetrustActiveGroups}
     */
    OnetrustActiveGroups: string
  }
}

/**
 * Cookie consent categories as defined by OneTrust
 * @see {@link https://lokalise.atlassian.net/wiki/spaces/~712020de26c712058445e784be2dedd7a5e012/pages/2975727652/Adding+Cookie+Consent+Banner+to+Lokalise+Flow#Existing-cookie-categories-used-by-lokalise.com|Lokalise consent categories}
 */
export const CookieConsentCategoryEnum = {
  Required: 'C0001',
  Functional: 'C0003',
  Performance: 'C0007',
} as const
export type CookieConsentCategory =
  (typeof CookieConsentCategoryEnum)[keyof typeof CookieConsentCategoryEnum]

interface GetCurrentCookieConsentParams {
  categories: CookieConsentCategory[]
}

/**
 * Check if the user has provided consent for the given categories.
 *
 * @param categories: cookie consent categories to check
 * @returns {boolean} true if the user has provided consent for all categories, false otherwise
 */
const hasProvidedCookieConsent = ({ categories }: GetCurrentCookieConsentParams) => {
  if (!window.OnetrustActiveGroups) {
    return false
  }
  return categories.every((category) => window.OnetrustActiveGroups.includes(category))
}

interface SubscribeToCookieConsentParams {
  categories: CookieConsentCategory[]
  onConsentProvided: () => void
}

/**
 * Subscribe to the OneTrustGroupsUpdated event and call the provided callback when the user has provided consent.
 *
 * @param {object} options
 * @param {CookieConsentCategory[]} options.categories - The cookie consent categories to check.
 * @param {() => void} options.onConsentProvided - The callback to call when the user has provided consent.
 * @returns {() => void} A cleanup function to remove the event listener.
 */
const subscribeToCookieConsent = ({
  categories,
  onConsentProvided,
}: SubscribeToCookieConsentParams): (() => void) => {
  // Provided callback should be called every time the OneTrustGroupsUpdated event is triggered
  // until the user has provided consent for the given categories. This is because the user
  // can still change their consent even after they initially reject the requested categories.
  const callback = () => {
    if (hasProvidedCookieConsent({ categories })) {
      // User has provided consent, remove the event listener to avoid calling the callback multiple times
      window.removeEventListener('OneTrustGroupsUpdated', callback)
      onConsentProvided()
    }
  }

  // From the OneTrust docs (https://my.onetrust.com/s/article/UUID-bc6c634c-b234-acc7-21d3-fa2819a6e679):
  // This event is called whenever consent is updated as well as on the initial load when consent becomes available.
  // Before a user interacts with the Banner or Preference Center, this event is raised with the default consent values.
  window.addEventListener('OneTrustGroupsUpdated', callback)

  return () => {
    window.removeEventListener('OneTrustGroupsUpdated', callback)
  }
}

/**
 * Wait for the user to provide consent for the given categories and call the provided callback when the user has provided consent.
 * If the user has already provided consent, the callback is called immediately.
 *
 * @param {object} options
 * @param {CookieConsentCategory[]} options.categories - Cookie consent categories to check.
 * @param {() => void} options.onConsentProvided - Callback to call when the user has provided consent.
 * @returns {(() => void) | undefined} A cleanup function to remove the event listener, or undefined if consent was already given.
 */
export const waitForCookieConsent = ({
  categories,
  onConsentProvided,
}: SubscribeToCookieConsentParams): (() => void) | undefined => {
  if (hasProvidedCookieConsent({ categories })) {
    onConsentProvided()
    return
  }

  return subscribeToCookieConsent({ categories, onConsentProvided })
}

/**
 * Open the OneTrust cookie preferences modal.
 *
 * @returns {void}
 */
export const openCookiePreferencesModal = () => {
  if (!window.OneTrust) {
    return
  }
  window.OneTrust.ToggleInfoDisplay()
}

/**
 * Hook to check if the OneTrust script has loaded, and use a function to open the OneTrust cookie preferences modal.
 *
 * @returns {boolean} cookieConsentReady: true if the OneTrust script has loaded, false otherwise
 * @returns {function} openCookiePreferencesModal: function to open the OneTrust cookie preferences modal
 */
export const useCookieConsentPreferences = () => {
  const [isCookieConsentReady, setIsCookieConsentReady] = useState(!!window.OneTrust)

  useEffect(() => {
    const callback = () => {
      setIsCookieConsentReady(true)
    }

    // From the OneTrust docs (https://my.onetrust.com/s/article/UUID-bc6c634c-b234-acc7-21d3-fa2819a6e679):
    // This event is called whenever consent is updated as well as on the initial load when consent becomes available.
    // Before a user interacts with the Banner or Preference Center, this event is raised with the default consent values.
    window.addEventListener('OneTrustGroupsUpdated', callback, { once: true })
    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', callback)
    }
  }, [])

  return {
    isCookieConsentReady,
    openCookiePreferencesModal,
  }
}
